import React, { useEffect, useRef, useState } from "react";
import {
  adminListThreads,
  adminGetThread,
  adminSendMessage,
  adminReactToMessage,
  pingAdminTyping,
  fetchAdminTypingStatus,
} from "../../lib/studioApi";
import { Button } from "../ui/button";
import {
  Loader2,
  Send,
  MessageCircle,
  Paperclip,
  X,
  Music,
  Video,
  Image as ImageIcon,
  FileText,
  Check,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import VoiceRecorder from "../studio/VoiceRecorder";
import MessageAttachments from "../studio/MessageAttachments";
import MessageReactions from "../studio/MessageReactions";
import TypingDots from "../studio/TypingDots";
import Avatar from "../studio/Avatar";
import EmojiButton from "../studio/EmojiButton";
import linkify from "../../lib/linkify";
import useDmAttachments from "../../hooks/useDmAttachments";
import useTypingPing from "../../hooks/useTypingPing";
import useNewMessageNotification from "../../hooks/useNewMessageNotification";

function safeTime(iso) {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
}

function KindIcon({ kind }) {
  if (kind === "audio") return <Music className="w-4 h-4" />;
  if (kind === "video") return <Video className="w-4 h-4" />;
  if (kind === "image") return <ImageIcon className="w-4 h-4" />;
  return <FileText className="w-4 h-4" />;
}

function Bubble({ msg, mine, isLast, onReact }) {
  const attachments = msg.attachments || [];
  const hasBody = msg.body && msg.body.trim();
  const showSeen = mine && isLast && msg.readByUser;
  return (
    <div className={`group flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className="max-w-[78%] md:max-w-[60%] w-full md:w-auto">
        {hasBody && (
          <div
            data-testid={`admin-bubble-body-${msg.id}`}
            className={`rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap break-words ${
              mine
                ? "bg-paul-yellow text-navy rounded-br-sm"
                : "bg-[#1b2138] text-white border border-white/5 rounded-bl-sm"
            }`}
            style={mine ? { color: "#0b1535" } : undefined}
          >
            {linkify(msg.body, {
              className: mine
                ? "underline decoration-navy/40 hover:decoration-navy break-words"
                : "underline decoration-paul-yellow/70 hover:decoration-paul-yellow text-paul-yellow break-words",
            })}
          </div>
        )}
        {attachments.length > 0 && (
          <div className={hasBody ? "mt-2" : ""}>
            <MessageAttachments attachments={attachments} mine={mine} />
          </div>
        )}
        <MessageReactions
          reactions={msg.reactions || []}
          myReactorId="admin"
          onToggle={(emoji) => onReact(msg.id, emoji)}
          align={mine ? "right" : "left"}
          theme="dark"
        />
        <div className={`text-[11px] mt-1 text-white/40 flex items-center gap-1 ${mine ? "justify-end" : "justify-start"}`}>
          <span>
            {msg.senderRole === "admin" ? "You" : msg.senderName} · {safeTime(msg.createdAt)}
          </span>
          {showSeen && (
            <span data-testid="admin-seen-indicator" className="flex items-center text-paul-yellow">
              <Check className="w-3 h-3 -mr-1.5" />
              <Check className="w-3 h-3" />
              <span className="ml-0.5">Seen</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminMessagesPanel() {
  const [threads, setThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const attachments = useDmAttachments({ asAdmin: true });
  const [userTyping, setUserTyping] = useState(false);
  const [voicePhase, setVoicePhase] = useState("idle");
  const pingTyping = useTypingPing(() =>
    selectedId ? pingAdminTyping(selectedId) : Promise.resolve(),
  );

  // Pop sound when the artist sends a new message into the active thread.
  // Reset baseline whenever the admin switches threads.
  useNewMessageNotification(
    thread?.messages,
    (m) => m && m.senderRole === "user",
    selectedId,
  );

  const insertEmoji = (emoji) => {
    const el = textareaRef.current;
    if (!el) {
      setText((t) => t + emoji);
      return;
    }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    const next = text.slice(0, start) + emoji + text.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + emoji.length;
        el.setSelectionRange(pos, pos);
      } catch {
        /* ignore */
      }
    });
  };

  // Poll threads list every 6s; auto-select first thread on first load.
  useEffect(() => {
    const tick = () => {
      adminListThreads()
        .then((data) => {
          setThreads(data);
          setLoadingThreads(false);
          setSelectedId((cur) => cur ?? (data[0]?.userId ?? null));
        })
        .catch(() => setLoadingThreads(false));
    };
    tick();
    const id = setInterval(tick, 6000);
    return () => clearInterval(id);
  }, []);

  // Load + poll the active thread when selectedId changes.
  useEffect(() => {
    if (!selectedId) return undefined;
    let cancelled = false;
    const tick = () => {
      adminGetThread(selectedId)
        .then((data) => {
          if (!cancelled) setThread(data);
        })
        .catch(() => {});
      fetchAdminTypingStatus(selectedId)
        .then((typing) => {
          if (!cancelled) setUserTyping(typing);
        })
        .catch(() => {});
    };
    tick();
    const id = setInterval(tick, 6000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [selectedId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread?.messages?.length]);

  const send = async () => {
    const body = text.trim();
    if (!selectedId || sending) return;
    const ready = attachments.pending.length === 0 || attachments.allDone;
    if (!ready) {
      toast.error("Wait for uploads to finish");
      return;
    }
    const atts = attachments.finalize();
    if (!body && atts.length === 0) return;
    setSending(true);
    try {
      const m = await adminSendMessage(selectedId, body, atts);
      setThread((t) => (t ? { ...t, messages: [...t.messages, m] } : t));
      setText("");
      attachments.clear();
      adminListThreads().then(setThreads).catch(() => {});
    } catch {
      toast.error("Send failed");
    } finally {
      setSending(false);
    }
  };

  const sendVoice = async (attachment) => {
    if (!selectedId) return;
    try {
      const m = await adminSendMessage(selectedId, "", [attachment]);
      setThread((t) => (t ? { ...t, messages: [...t.messages, m] } : t));
      adminListThreads().then(setThreads).catch(() => {});
    } catch {
      toast.error("Voice send failed");
    }
  };

  const onReact = async (messageId, emoji) => {
    try {
      const updated = await adminReactToMessage(messageId, emoji);
      setThread((t) =>
        t ? { ...t, messages: t.messages.map((m) => (m.id === messageId ? updated : m)) } : t,
      );
    } catch {
      toast.error("Reaction failed");
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div data-testid="admin-messages-panel">
      <div className="mb-5">
        <h2 className="text-2xl font-extrabold text-white">Messages</h2>
        <p className="text-white/50 text-sm mt-1">
          Direct conversations with your studio artists.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4 h-[calc(100vh-260px)] min-h-[480px]">
        {/* Threads list */}
        <aside
          data-testid="threads-list"
          className="bg-[#0f1322] rounded-xl border border-white/5 overflow-y-auto"
        >
          {loadingThreads ? (
            <div className="text-center text-white/40 py-10">
              <Loader2 className="w-5 h-5 animate-spin inline-block" />
            </div>
          ) : threads.length === 0 ? (
            <div className="text-center text-white/40 py-10 px-4">
              <MessageCircle className="w-8 h-8 mx-auto mb-2 text-white/20" />
              <p className="text-sm">No studio users yet.</p>
            </div>
          ) : (
            <ul>
              {threads.map((t) => {
                const isActive = t.userId === selectedId;
                const display = t.user.artistName || t.user.name;
                return (
                  <li key={t.userId}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(t.userId)}
                      data-testid={`thread-row-${t.userId}`}
                      className={`w-full text-left px-4 py-3 border-b border-white/5 flex items-start gap-3 transition-colors ${
                        isActive ? "bg-paul-yellow/10" : "hover:bg-white/[0.03]"
                      }`}
                    >
                      <Avatar user={t.user} size="md" showDot ringColor="border-[#0f1322]" testIdPrefix={`thread-avatar-${t.userId}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold text-white truncate">{display}</div>
                          {t.unread > 0 && (
                            <span
                              data-testid={`thread-unread-${t.userId}`}
                              className="bg-red-500 text-white text-[11px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center flex-shrink-0"
                            >
                              {t.unread > 9 ? "9+" : t.unread}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-white/50 truncate">
                          {t.lastMessage
                            ? `${
                                t.lastMessage.senderRole === "admin" ? "You: " : ""
                              }${t.lastMessage.body || "(attachment)"}`
                            : "No messages yet"}
                        </div>
                        {t.lastMessage && (
                          <div className="text-[11px] text-white/40 mt-0.5">
                            {safeTime(t.lastMessage.createdAt)}
                          </div>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {/* Active thread */}
        <section className="bg-[#10162a] rounded-xl border border-white/5 flex flex-col overflow-hidden">
          {!selectedId ? (
            <div className="flex-1 flex items-center justify-center text-white/40 text-sm">
              Select a conversation
            </div>
          ) : (
            <>
              <header className="px-5 py-3 border-b border-white/5 flex items-center gap-3">
                {thread?.user && (
                  <>
                    <Avatar user={thread.user} size="md" showDot ringColor="border-[#10162a]" testIdPrefix="thread-header-avatar" />
                    <div className="flex-1">
                      <div className="font-semibold text-white">
                        {thread.user.artistName || thread.user.name}
                      </div>
                      {userTyping ? (
                        <div className="text-xs text-paul-yellow font-semibold flex items-center gap-1.5" data-testid="admin-typing-indicator">
                          <TypingDots theme="dark" />
                          <span>{thread.user.artistName || thread.user.name} is typing…</span>
                        </div>
                      ) : (
                        <div className="text-xs text-white/50">
                          {thread.user.email} · {thread.user.status}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </header>

              <div
                className="flex-1 overflow-y-auto px-4 md:px-6 py-5 space-y-3 bg-[#10162a]"
                data-testid="thread-messages"
              >
                {!thread ? (
                  <div className="text-center text-white/40 py-10">
                    <Loader2 className="w-5 h-5 animate-spin inline-block" />
                  </div>
                ) : thread.messages.length === 0 ? (
                  <div className="text-center text-white/40 py-16 text-sm">
                    No messages in this conversation yet.
                  </div>
                ) : (
                  thread.messages.map((m, i) => {
                    const isLastMine =
                      m.senderRole === "admin" &&
                      !thread.messages.slice(i + 1).some((x) => x.senderRole === "admin");
                    return (
                      <Bubble
                        key={m.id}
                        msg={m}
                        mine={m.senderRole === "admin"}
                        isLast={isLastMine}
                        onReact={onReact}
                      />
                    );
                  })
                )}
                <div ref={endRef} />
              </div>

              <div className="p-3 border-t border-white/5 bg-[#0b0f1e]">
                {attachments.pending.length > 0 && (
                  <div className="space-y-2 mb-2" data-testid="admin-pending-attachments">
                    {attachments.pending.map((p) => (
                      <div
                        key={p.id}
                        className="flex items-center gap-3 bg-[#161a2c] rounded-lg p-2 border border-white/5"
                      >
                        <div className="w-7 h-7 rounded-md bg-paul-yellow/15 text-paul-yellow flex items-center justify-center flex-shrink-0">
                          <KindIcon kind={p.kind} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-white truncate font-medium">{p.file.name}</div>
                          <div className="h-1 bg-white/10 rounded-full mt-1 overflow-hidden">
                            <div className="h-full bg-paul-yellow transition-all" style={{ width: `${p.progress}%` }} />
                          </div>
                        </div>
                        <span className="text-[10px] text-white/50 font-mono min-w-[32px] text-right">{p.progress}%</span>
                        <button type="button" onClick={() => attachments.removePending(p.id)} className="text-white/40 hover:text-red-400">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-2">
                  {voicePhase === "idle" && (
                    <>
                      <button
                        type="button"
                        data-testid="admin-attach-btn"
                        onClick={() => fileInputRef.current?.click()}
                        className="h-11 w-11 rounded-xl border border-white/10 text-white/70 hover:text-paul-yellow hover:border-paul-yellow/60 flex-shrink-0 flex items-center justify-center transition-colors"
                        title="Attach files"
                      >
                        <Paperclip className="w-5 h-5" />
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        hidden
                        onChange={(e) => {
                          if (e.target.files) attachments.addFiles(e.target.files);
                          e.target.value = "";
                        }}
                      />
                    </>
                  )}
                  <VoiceRecorder asAdmin onSend={sendVoice} theme="dark" onPhaseChange={setVoicePhase} />
                  {voicePhase === "idle" && (
                    <>
                      <EmojiButton
                        theme="dark"
                        testId="admin-emoji-btn"
                        onPick={insertEmoji}
                      />
                      <textarea
                        ref={textareaRef}
                        data-testid="admin-message-input"
                        value={text}
                        onChange={(e) => {
                          setText(e.target.value);
                          if (e.target.value.trim()) pingTyping();
                        }}
                        onKeyDown={onKey}
                        placeholder="Reply…"
                        rows={1}
                        className="flex-1 resize-none bg-[#10162a] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-paul-yellow/60 max-h-32"
                        style={{ minHeight: 44 }}
                      />
                      <Button
                        data-testid="admin-message-send"
                        onClick={send}
                        disabled={sending || (!text.trim() && attachments.pending.length === 0)}
                        className="h-11 px-4 bg-paul-yellow hover:bg-paul-yellow text-navy font-bold gap-2"
                        style={{ color: "#0b1535" }}
                      >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        <span className="hidden sm:inline">Send</span>
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
